import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { moviesData } from '@/mocks/movies';
import { findById, formatViews, getRelated, typeLabel } from '@/utils/movie';
import { useSeo } from '@/hooks/useSeo';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import ShareModal from '@/pages/detail/components/ShareModal';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? findById(moviesData, id) : undefined;

  const [shareOpen, setShareOpen] = useState(false);
  const [collected, setCollected] = useState(false);

  useEffect(() => {
    setShareOpen(false);
    setCollected(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  useSeo({
    title: movie ? `${movie.title} - 影视详情 - 牛牛视频` : '影视详情 - 牛牛视频',
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）${movie.summary.slice(0, 90)}`
      : '牛牛视频影视详情页',
    keywords: movie ? `${movie.title},${movie.category},牛牛视频,影视详情` : '牛牛视频,影视详情',
  });

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 pt-28 md:pt-32 pb-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-movie-2-line text-3xl text-foreground-400" />
        </span>
        <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-500">该内容可能已下架或链接有误</p>
        <Link
          to="/"
          className="mt-6 h-10 px-5 inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(moviesData, movie, 6);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="w-full pt-16 md:pt-20">
      {/* 顶部信息区 */}
      <section className="w-full bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200 flex-wrap">
            <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.type}`}
              className="hover:text-background-50 transition-colors cursor-pointer"
            >
              {typeLabel(movie.type)}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50 line-clamp-1">{movie.title}</span>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            {/* 封面 */}
            <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-800">
                <img
                  src={movie.cover}
                  alt={movie.title}
                  title={`${movie.title} - 牛牛视频`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* 元信息 */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-accent-950 text-xs font-bold">
                  {typeLabel(movie.type)}
                </span>
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 text-2xl md:text-4xl font-black text-background-50">
                {movie.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-100">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="font-bold text-background-50">{movie.rating.toFixed(1)}</span>
                  <span className="text-primary-200">分</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line" />
                  {formatViews(movie.views)}次播放
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {movie.year}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line" />
                  {movie.region}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-refresh-line" />
                  {movie.status}
                </span>
              </div>

              <dl className="mt-5 space-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="shrink-0 w-12 text-primary-300">类型</dt>
                  <dd className="text-background-100">{movie.category}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="shrink-0 w-12 text-primary-300">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="shrink-0 w-12 text-primary-300">主演</dt>
                  <dd className="text-background-100">{movie.cast.join('、')}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCollected((v) => !v)}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-bold whitespace-nowrap transition-colors cursor-pointer ${
                    collected
                      ? 'bg-accent-500 text-accent-950'
                      : 'bg-background-50/15 hover:bg-background-50/25 border border-background-50/40 text-background-50'
                  }`}
                >
                  <i className={collected ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'} />
                  {collected ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 hover:bg-background-50/25 border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line text-lg" />
                  分享
                </button>
                <span className="text-xs text-primary-300">
                  {collected ? '已加入收藏（演示功能，不会保存）' : '收藏与分享均为前端演示'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 简介 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-primary-500" />
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">影视简介</h2>
          </div>
          <p className="text-sm md:text-base text-foreground-600 leading-loose">{movie.summary}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {movie.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs">
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/70 text-xs md:text-sm text-foreground-500">
            <i className="ri-information-line mr-1" />
            本站为影视内容聚合与展示平台，暂不提供视频播放与下载功能，以上内容均为演示资料。
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="w-full bg-background-100 border-t border-background-200/70">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <SectionHeader
              title="相关推荐"
              subtitle="同类题材 · 相似主演的影视作品"
              moreLink={`/category/${movie.type}`}
              moreText={`更多${typeLabel(movie.type)}`}
              accent="accent"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={movie.title}
        url={shareUrl}
      />
    </div>
  );
}